
import React, { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { useParams } from "react-router-dom"
import 'chartjs-adapter-date-fns';
import { SERVER_HOST } from '../settings';

const random = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}
const range = (size) => Array.from(new Array(size)).map((_, i) => i);
const SAMPLE_SIZE = 20;

const loadUserHistoryData = (userId) => {
    const url = `${SERVER_HOST}/userhistory/${userId}`;
    const options = {
    };
    return fetch(url, options).then(data => data.json());
    
}

const toDate = (dateString) => {
    if (!dateString) {
        return null;
    }

    const parsed = new Date(dateString);
    if (isNaN(parsed)) {
        return null;
    }

    const yyyyMmDd = parsed.toISOString().split('T')[0];
    return new Date(yyyyMmDd);
}

const getDaysArray = function(start, end) {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const arr = [];

    for (let dt = new Date(startDate); dt <= endDate; dt.setUTCDate(dt.getUTCDate() + 1)) {
        arr.push(new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate())));
    }

    return arr;
};

export default function PlayerChart({rankingData = {}, datasetName = 'rating', reverseScale = false, minValue = null}) {
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});

    const [userHistoryData, setUserHistoryData] = useState({});

    const { playerId } = useParams();

    const createChartData = (userHistoryData) => {

        const dates = Object.values((userHistoryData||{})[datasetName]||{}).reduce((acc, x)=>([...acc, ...Object.keys(x)]), []).map(x=>new Date(x));
        
        if (dates?.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        }
        
        const startDate = new Date(Math.min.apply(null, dates));
        const endDate = new Date(Math.max.apply(null, dates));
        
        const labels = getDaysArray(startDate, endDate).map(date =>
            date.toISOString().split('T')[0]
        );

        const fillMissingDates = (data, startDate, endDate) => {
            const filledData = {};
            const currentDate = new Date(startDate);
            const lastDate = new Date(endDate);
            let previousValue = null;
        
            const toStr = (date) => date.toISOString().split('T')[0];
            while (toStr(currentDate) <= toStr(lastDate)) {
                const dateStr = toStr(currentDate);
        
                if (data[dateStr] !== undefined) {
                    previousValue = data[dateStr];
                }
        
                filledData[dateStr] = previousValue;
                currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
            }
        
            return filledData;
        }
        
        const lineColors = {
            overall: '--blue-500',
            moving: '--pink-500',
            nomove: '--green-500',
            nmpz: '--yellow-500'
        };
        const labelText = {
            overall: 'Overall',
            moving: 'Moving',
            nomove: 'No move',
            nmpz: 'NMPZ'
        };
        const createSet = (gameMode, data, startDate, endDate) => {
            const documentStyle = getComputedStyle(document.documentElement);
            return {
                label: labelText[gameMode],
                data: Object.values(fillMissingDates(data, startDate, endDate)),
                fill: false,
                borderColor: documentStyle.getPropertyValue(lineColors[gameMode]),
                tension: 0.4,
                // hidden: gameMode !== 'overall',
                pointHoverRadius: 4,
                pointHoverBackgroundColor: '#60a5fa',
                pointHoverBorderColor: '#ffffff',
                pointHoverBorderWidth: 3
            }
        }

        // const dataSets = Object.entries(sampleData.ratings).map(([gameMode, data]) => createSet(gameMode, data, startDate, endDate));
        const dataSets = Object.entries((userHistoryData||{})[datasetName]||[]).map(([gameMode, data]) => createSet(gameMode, data, startDate, endDate));

        return {
            labels,
            datasets: dataSets
        };
    }

    useEffect(() => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color');
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border');
        // const {overall, moving, nomove, nmpz} = rankingData;
        
        const NUMBER_OF_DAYS = 190;
        const START_DATE = '2025-01-15';
        let date = new Date(START_DATE);

        const sampleData2 = {
            rating: {
                overall: {},
                nomove: {},
                nmpz: {}
            }
        }

        for (let i = 0; i <= NUMBER_OF_DAYS; i++) {
            const dateString = date.toISOString().split('T')[0];
            sampleData2['rating']['overall'][dateString] = random(740, 950);
            sampleData2['rating']['nomove'][dateString] = random(740, 950);
            sampleData2['rating']['nmpz'][dateString] = random(740, 950);
            date.setDate(date.getDate() + 1);
        }

        const sampleData = {
            rating: {
                overall: {
                    '2024-01-01': random(740, 950),
                    '2024-01-02': random(740, 950),
                    '2024-01-03': random(740, 950),
                    '2024-01-05': random(740, 950),
                    '2024-01-06': random(740, 950),
                    '2024-01-07': random(740, 950),
                    '2024-01-08': random(740, 950),
                    '2024-01-10': random(740, 950),
                    '2024-01-12': random(740, 950),
                    '2024-01-13': random(740, 950),
                    '2024-01-14': random(740, 950),
                    '2024-01-15': random(740, 950)
                },
                nomove: {
                    '2024-01-01': random(740, 950),
                    '2024-01-02': random(740, 950),
                    '2024-01-04': random(740, 950),
                    '2024-01-07': random(740, 950),
                    '2024-01-08': random(740, 950),
                    '2024-01-09': random(740, 950),
                    '2024-01-10': random(740, 950),
                    '2024-01-11': random(740, 950),
                    '2024-01-12': random(740, 950),
                    '2024-01-16': random(740, 950),
                    '2024-01-17': random(740, 950),
                    '2024-01-18': random(740, 950)
                },
                nmpz: {
                    '2024-01-01': random(740, 950),
                    '2024-01-02': random(740, 950),
                    '2024-01-04': random(740, 950),
                    '2024-01-07': random(740, 950),
                    '2024-01-08': random(740, 950),
                    '2024-01-09': random(740, 950),
                    '2024-01-10': random(740, 950),
                    '2024-01-11': random(740, 950),
                    '2024-01-12': random(740, 950),
                    '2024-01-16': random(740, 950),
                    '2024-01-17': random(740, 950),
                    '2024-01-18': random(740, 950)
                }
            }
        }

        // console.log(userHistoryData)

        const options = {
            maintainAspectRatio: false,
            aspectRatio: 0.6,
            tension: 0.2,
            interaction: { mode: "nearest", axis: "x", intersect: false },
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                },
                tooltip: {
                    mode: "index",
                    intersect: !1,
                    backgroundColor: "rgba(30, 41, 59, 0.95)",
                    titleColor: "#ffffff",
                    bodyColor: "#ffffff",
                    borderColor: "#60a5fa",
                    borderWidth: 1,
                    cornerRadius: 8,
                    displayColors: !1,
                    callbacks: {
                        label: function(e) {
                            return [`${e.dataset.label}: ${e?.dataset.data[e.dataIndex]}`]
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder
                    },
                    // type: 'time',
                    // time: {
                    //     unit: 'day',    // Show per day (or use 'month', 'week')
                        // displayFormats: {
                        //     day: 'MMM dd' // 📅 Format (e.g., "Feb 12")
                        // }
                    // },
                    // ticks: {
                    //     autoSkip: true, // ✨ Skips overlapping labels
                    //     maxTicksLimit: 7 // ⏱️ Limits number of ticks shown
                    // }
                },
                y: {
                    ticks: {
                        color: textColorSecondary,
                        precision: 0
                    },
                    grace: '5%',
                    grid: {
                        color: surfaceBorder
                    },
                    reverse: reverseScale,
                    min: minValue
                }
            }
        };
        setChartData(createChartData(userHistoryData));
        setChartOptions(options);
    }, [userHistoryData]);


        useEffect(() => {
            loadUserHistoryData(playerId).then(data => {
                setUserHistoryData(data);
                setChartData(createChartData(userHistoryData));
            })
        }, []);

    return (
        <div className="card">
            <Chart type="line" data={chartData} options={chartOptions} />
        </div>
    )
}